// Library guide — uploading files and browsing My Files / Org Files.
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const libraryGuide = {
  id: "library",
  icon: "photo_library",
  keywords: [
    "library",
    "kütüphane",
    "upload",
    "yükle",
    "my files",
    "dosyalarım",
    "org files",
    "kurum dosyaları",
    "ortak dosyalar",
    "brand",
    "marka",
    "media",
    "medya",
    "import",
    "içe aktar",
    "içeri aktar",
    "file",
    "dosya",
    "add to scene",
    "sahneye ekle",
    "file size",
    "dosya boyutu",
    "filter by scene",
    "sahneye göre filtrele",
    "grouped view",
    "gruplu görünüm",
    "click sound",
    "tıklama sesi",
    "scene sound",
    "sahne sesi",
    "replace file",
    "dosyayı değiştir",
    "biblioteca",
    "subir archivos",
    "mis archivos",
    "archivos de la organización",
    "importar",
    "archivo",
    "añadir a la escena",
    "tamaño de archivo",
    "filtrar por escena",
    "vista agrupada",
    "sonido de clic",
    "sonido de escena",
    "reemplazar archivo",
  ],
  content: {
    en: {
      feature: "Library",
      title: "Library & uploads",
      sections: [
        {
          id: "overview",
          title: "Where the Library lives",
          videoUrl: null,
          text:
            "Click the **Library** button in the top bar of the editor to open a panel on the left for bringing media into your training. You can also open it with **Import** in the **⋮** menu. Dragging a file from your computer anywhere onto the editor opens it directly on **Upload Files**.\n\n" +
            "Inside, four tabs cover everything:\n\n" +
            "- **Upload Files**: send new files from your device.\n" +
            "- **My Files**: everything you've uploaded or generated yourself.\n" +
            "- **Org Files**: files shared across your organization, including media generated in other trainings.\n" +
            "- **Brand**: your organization's brand library (logos, fonts, colors, and more). See the Brand kit guide.\n\n" +
            "Picking a tab swaps the panel's content in place. Close the panel with the × in its corner or the arrow tab on its edge.",
        },
        {
          id: "upload",
          title: "Uploading files",
          videoUrl: null,
          text:
            "On **Upload Files**, drop files onto the drop area or click it to browse. You can select several files at once, and each one gets its own row in a queue. Supported types are images, audio, video, PDF, and CSV.\n\n" +
            "- Click **Upload file** to send everything in the queue. Each row shows its own progress, and you can remove a row with × before it uploads.\n" +
            "- Videos can be up to 500 MB, and all other files up to 100 MB. A file over the limit is flagged in its row and isn't uploaded. Formats like AVI or MKV are converted to MP4 during upload.\n" +
            "- When a row finishes, use its **+** button (**Add to Scene**) to place it on the current scene. If several files finished, use **Add All to Scene**. **Clear** empties the queue.\n" +
            "- If an upload fails, the reason appears in its row. The error icon opens the technical details, which you can copy and send to support.\n" +
            "- Every upload is also saved to your account, so you'll find it again later under **My Files**.\n\n" +
            "The second tab here, **Screen Training**, imports a screen-recorder ZIP. See the Screen Training guide.",
        },
        {
          id: "use-files",
          title: "Browsing and inserting files",
          videoUrl: null,
          text:
            "**My Files** holds everything tied to your account. **Org Files** adds everything shared across your organization, and a toggle next to its search box switches between **List View** and **Grouped View** (files grouped by the training they came from).\n\n" +
            "- Search, or quick-filter by type (**All**, **Image**, **Audio**, **Video**, **PDF**, **CSV**). Tap **+** for more filters: **Avatar**, **Clone Voice**, **Storyboard Videos**, and **Animated Image**.\n" +
            "- In **My Files**, the clapperboard button next to the search box filters by scene. Pick **All Files** or a specific scene to see only the files used there.\n" +
            "- **Click** a file to insert it into the current scene, or **drag** it straight onto the canvas. Use **Load More** at the bottom to see older files.\n" +
            "- Hover a card for **Preview** (**Listen**, for audio) and **Download**. In **My Files**, cards also have **Delete**, which asks for confirmation first.",
        },
        {
          id: "audio-files",
          title: "Audio files: listen and add as a sound",
          videoUrl: null,
          text:
            "With the **Audio** or **Clone Voice** filter on, **My Files** and **Org Files** show audio as a list instead of tiles.\n\n" +
            "- Each row has a play button and a progress bar that you can click to jump to any point, plus the elapsed and total time.\n" +
            "- **Add as click sound** puts the audio on the canvas as an Audio Cue element. **Add as scene sound** adds it to the scene's sound track instead.\n" +
            "- **Download** is available on every row. **Delete** appears only in **My Files**.",
        },
        {
          id: "replace-file",
          title: "Replacing a file from the Library",
          videoUrl: null,
          text:
            "To swap the file behind an image, video, audio, or PDF element without rebuilding it, select the element and choose **Replace File** in the right panel.\n\n" +
            "- The Library opens on **My Files** (or on **Org Files**, if the file originally came from there), already filtered to the element's file type.\n" +
            "- Click a file, and it replaces the old one in place. The element keeps its position and size.\n" +
            "- Icons, animations, characters, and YouTube videos open their own category in **Components** instead.",
        },
      ],
    },
    tr: {
      feature: "Kütüphane",
      title: "Kütüphane ve dosya yükleme",
      sections: [
        {
          id: "overview",
          title: "Kütüphane nerede",
          videoUrl: null,
          text:
            "Eğitiminize medya eklemek için editörün üst çubuğundaki **Kütüphane** düğmesine tıklayın. Solda ayrı bir panel açılır. Aynı panele **⋮** menüsündeki **İçeri Aktar** seçeneğiyle de ulaşabilirsiniz. Bilgisayarınızdan bir dosyayı editörün herhangi bir yerine sürüklediğinizde panel doğrudan **Dosya Yükle** sekmesinde açılır.\n\n" +
            "İçerideki dört sekme her şeyi kapsar:\n\n" +
            "- **Dosya Yükle**: cihazınızdan yeni dosya gönderin.\n" +
            "- **Dosyalarım**: kendi yüklediğiniz veya oluşturduğunuz her şey.\n" +
            "- **Ortak Dosyalar**: kurumunuzda paylaşılan dosyalar. Diğer eğitimlerde üretilen medyalar da buradadır.\n" +
            "- **Marka**: kurumunuzun marka kütüphanesi (logolar, yazı tipleri, renkler ve daha fazlası). Ayrıntılar için Marka kiti rehberine bakın.\n\n" +
            "Bir sekme seçtiğinizde panelin içeriği yerinde değişir. Paneli köşesindeki × ile ya da kenarındaki ok sekmesiyle kapatabilirsiniz.",
        },
        {
          id: "upload",
          title: "Dosya yükleme",
          videoUrl: null,
          text:
            "**Dosya Yükle** sekmesinde dosyaları bırakma alanına sürükleyin ya da göz atmak için alana tıklayın. Aynı anda birden çok dosya seçebilirsiniz. Her dosya kuyrukta kendi satırını alır. Desteklenen türler görsel, ses, video, PDF ve CSV'dir.\n\n" +
            "- Kuyruktaki her şeyi göndermek için **Yükle** düğmesine tıklayın. Her satırda kendi ilerlemesi görünür. Yüklenmeden önce bir satırı × ile kaldırabilirsiniz.\n" +
            "- Videolar en fazla 500 MB, diğer dosyalar en fazla 100 MB olabilir. Sınırı aşan dosya satırında işaretlenir ve yüklenmez. AVI veya MKV gibi biçimler yükleme sırasında MP4'e dönüştürülür.\n" +
            "- Biten bir satırı mevcut sahneye yerleştirmek için satırdaki **+** (**Sahneye Ekle**) düğmesini kullanın. Birden fazla dosya bittiyse **Tümünü Sahneye Ekle** düğmesini kullanın. **Temizle** kuyruğu boşaltır.\n" +
            "- Bir yükleme başarısız olursa nedeni satırında yazar. Hata simgesi teknik ayrıntıları açar. Bu ayrıntıları kopyalayıp destek ekibine gönderebilirsiniz.\n" +
            "- Her yükleme hesabınıza da kaydedilir. Dosyayı sonra **Dosyalarım** altında yine bulursunuz.\n\n" +
            "Buradaki ikinci sekme olan **Ekran Eğitimi**, ekran kaydedici ZIP dosyasını içe aktarır. Ayrıntılar için Ekran Eğitimi rehberine bakın.",
        },
        {
          id: "use-files",
          title: "Dosyalara göz atma ve ekleme",
          videoUrl: null,
          text:
            "**Dosyalarım** hesabınıza ait her şeyi barındırır. **Ortak Dosyalar** buna kurumunuzda paylaşılan her şeyi ekler. Arama kutusunun yanındaki düğme **Liste Görünümü** ile **Gruplu Görünüm** (dosyalar geldikleri eğitime göre gruplanır) arasında geçiş yapar.\n\n" +
            "- Arama yapın ya da türe göre hızlı filtreleyin (**Hepsi**, **Resim**, **Ses**, **Video**, **PDF**, **CSV**). Daha fazla filtre için **+** işaretine dokunun: **Avatar**, **Klon Ses**, **Storyboard Videoları** ve **Hareketli Resim**.\n" +
            "- **Dosyalarım** içinde arama kutusunun yanındaki klaket düğmesi sahneye göre filtreler. Yalnızca o sahnede kullanılan dosyaları görmek için **Tüm Dosyalar** ya da bir sahne seçin.\n" +
            "- Bir dosyayı mevcut sahneye eklemek için üzerine **tıklayın** ya da doğrudan tuvale **sürükleyin**. Daha eski dosyaları görmek için alttaki **Daha Fazla Yükle** düğmesini kullanın.\n" +
            "- Bir kartın üzerine geldiğinizde **Ön izleme** (ses için **Dinle**) ve **İndir** görünür. **Dosyalarım** kartlarında ayrıca **Sil** vardır ve silmeden önce onay ister.",
        },
        {
          id: "audio-files",
          title: "Ses dosyaları: dinleme ve ses olarak ekleme",
          videoUrl: null,
          text:
            "**Ses** veya **Klon Ses** filtresi açıkken **Dosyalarım** ve **Ortak Dosyalar** sesleri kutucuk yerine liste olarak gösterir.\n\n" +
            "- Her satırda bir oynatma düğmesi, istediğiniz noktaya atlamak için tıklayabileceğiniz bir ilerleme çubuğu ve geçen/toplam süre bulunur.\n" +
            "- **Tıklama sesi olarak ekle**, sesi tuvale Ses İşareti öğesi olarak koyar. **Sahne sesi olarak ekle** ise sahnenin ses kanalına ekler.\n" +
            "- **İndir** her satırda bulunur. **Sil** yalnızca **Dosyalarım** içinde görünür.",
        },
        {
          id: "replace-file",
          title: "Kütüphaneden dosya değiştirme",
          videoUrl: null,
          text:
            "Bir görsel, video, ses veya PDF öğesinin dosyasını öğeyi baştan kurmadan değiştirmek için öğeyi seçin ve sağ paneldeki **Dosyayı Değiştir**'i seçin.\n\n" +
            "- Kütüphane, öğenin dosya türüne göre filtrelenmiş olarak **Dosyalarım**'da açılır. Dosya ilk olarak oradan geldiyse **Ortak Dosyalar**'da açılır.\n" +
            "- Bir dosyaya tıkladığınızda eskisinin yerine geçer. Öğe konumunu ve boyutunu korur.\n" +
            "- İkonlar, animasyonlar, karakterler ve YouTube videoları ise **Bileşenler** içindeki kendi kategorilerinde açılır.",
        },
      ],
    },
    es: {
      feature: "Biblioteca",
      title: "Biblioteca y subida de archivos",
      sections: [
        {
          id: "overview",
          title: "Dónde está la Biblioteca",
          videoUrl: null,
          text:
            "Haz clic en el botón **Biblioteca** de la barra superior del editor para abrir un panel a la izquierda desde el que añadir contenido multimedia a tu formación. También puedes abrirlo con **Importar** en el menú **⋮**. Si arrastras un archivo desde tu ordenador a cualquier parte del editor, el panel se abre directamente en **Subir Archivos**.\n\n" +
            "Dentro, cuatro pestañas lo cubren todo:\n\n" +
            "- **Subir Archivos**: envía archivos nuevos desde tu dispositivo.\n" +
            "- **Mis Archivos**: todo lo que has subido o generado tú.\n" +
            "- **Arch. Org.**: archivos compartidos en tu organización, incluido el contenido multimedia generado en otras formaciones.\n" +
            "- **Marca**: la biblioteca de marca de tu organización (logotipos, fuentes, colores y más). Consulta la guía del kit de marca.\n\n" +
            "Al elegir una pestaña, el contenido del panel cambia en el mismo sitio. Cierra el panel con la × de su esquina o con la pestaña de flecha de su borde.",
        },
        {
          id: "upload",
          title: "Subir archivos",
          videoUrl: null,
          text:
            "En **Subir Archivos**, suelta los archivos en la zona de carga o haz clic en ella para buscarlos. Puedes seleccionar varios archivos a la vez y cada uno ocupa su propia fila en una cola. Se admiten imágenes, audio, vídeo, PDF y CSV.\n\n" +
            "- Haz clic en **Subir Fichero** para enviar todo lo que hay en la cola. Cada fila muestra su propio progreso, y puedes quitar una fila con la × antes de que se suba.\n" +
            "- Los vídeos pueden pesar hasta 500 MB y el resto de archivos hasta 100 MB. Un archivo que supera el límite se marca en su fila y no se sube. Los formatos como AVI o MKV se convierten a MP4 durante la subida.\n" +
            "- Cuando una fila termina, usa su botón **+** (**Añadir a la escena**) para colocarla en la escena actual. Si han terminado varios archivos, usa **Añadir todo a la escena**. **Borrar** vacía la cola.\n" +
            "- Si una subida falla, el motivo aparece en su fila. El icono de error abre los detalles técnicos, que puedes copiar y enviar al equipo de soporte.\n" +
            "- Cada subida también se guarda en tu cuenta, así que después la encontrarás en **Mis Archivos**.\n\n" +
            "La segunda pestaña de esta sección, **Formación en pantalla**, importa un ZIP de la grabadora de pantalla. Consulta la guía de formación en pantalla.",
        },
        {
          id: "use-files",
          title: "Explorar e insertar archivos",
          videoUrl: null,
          text:
            "**Mis Archivos** reúne todo lo vinculado a tu cuenta. **Arch. Org.** añade todo lo compartido en tu organización, y un interruptor junto a su cuadro de búsqueda cambia entre **Vista de Lista** y **Vista Agrupada** (archivos agrupados según la formación de la que proceden).\n\n" +
            "- Busca o filtra rápidamente por tipo (**Todos**, **Imagen**, **Audio**, **Video**, **PDF**, **CSV**). Pulsa **+** para ver más filtros: **Avatar**, **Clonar voz**, **Vídeos de storyboard** y **Imagen animada**.\n" +
            "- En **Mis Archivos**, el botón de la claqueta junto al cuadro de búsqueda filtra por escena. Elige **Todos los archivos** o una escena concreta para ver solo los archivos que se usan en ella.\n" +
            "- **Haz clic** en un archivo para insertarlo en la escena actual o **arrástralo** directamente al lienzo. Usa **Cargar Más** en la parte inferior para ver archivos más antiguos.\n" +
            "- Pasa el cursor sobre una tarjeta para ver **Vista Previa** (**Escuchar**, en el audio) y **Descargar**. En **Mis Archivos**, las tarjetas también tienen **Eliminar**, que pide confirmación antes.",
        },
        {
          id: "audio-files",
          title: "Archivos de audio: escuchar y añadir como sonido",
          videoUrl: null,
          text:
            "Con el filtro **Audio** o **Clonar voz** activado, **Mis Archivos** y **Arch. Org.** muestran el audio como una lista en lugar de miniaturas.\n\n" +
            "- Cada fila tiene un botón de reproducción y una barra de progreso en la que puedes hacer clic para saltar a cualquier punto, además del tiempo transcurrido y el total.\n" +
            "- **Añadir como sonido de clic** coloca el audio en el lienzo como un elemento de sonido (Audio Cue). **Añadir como sonido de escena** lo añade, en cambio, a la pista de sonido de la escena.\n" +
            "- **Descargar** está disponible en todas las filas. **Eliminar** solo aparece en **Mis Archivos**.",
        },
        {
          id: "replace-file",
          title: "Reemplazar un archivo desde la Biblioteca",
          videoUrl: null,
          text:
            "Para cambiar el archivo de un elemento de imagen, vídeo, audio o PDF sin tener que volver a crearlo, selecciona el elemento y elige **Reemplazar archivo** en el panel derecho.\n\n" +
            "- La Biblioteca se abre en **Mis Archivos** (o en **Arch. Org.**, si el archivo procedía de allí), ya filtrada por el tipo de archivo del elemento.\n" +
            "- Haz clic en un archivo y sustituirá al anterior en el mismo sitio. El elemento conserva su posición y su tamaño.\n" +
            "- Los iconos, las animaciones, los personajes y los vídeos de YouTube abren en su lugar su propia categoría en **Componentes**.",
        },
      ],
    },
  },
};

export default libraryGuide;
